import React, { memo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { ArrowLeft, Share2, Facebook, Twitter, Linkedin } from 'lucide-react';
export const BlogPost = () => {
  const {
    slug
  } = useParams();
  // In a real app, fetch post data based on slug
  const post = {
    title: 'Why AI Literacy is the New Coding',
    author: 'Dr. Sarah Johnson',
    role: 'Head of Curriculum',
    date: 'Oct 12, 2023',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80',
    content: `
      <p class="mb-6">In the early 2000s, the push was to teach every child to code. The logic was sound: software was eating the world, and digital literacy meant understanding the language of computers.</p>
      
      <p class="mb-6">Today, we stand at a similar precipice with Artificial Intelligence. But the paradigm has shifted. We don't just need children who can write code; we need children who can collaborate with intelligent systems, understand their limitations, and guide their ethical deployment.</p>
      
      <h2 class="text-2xl font-bold text-[#0A2540] mb-4 mt-8">The Shift from Syntax to Semantics</h2>
      
      <p class="mb-6">Coding is about syntax—giving explicit instructions. AI is about semantics—giving goals and context. As AI tools become more capable, the skill of "prompt engineering" or "AI orchestration" becomes more valuable than rote memorization of syntax.</p>
      
      <p class="mb-6">At AI For Kids Africa, we focus on three core pillars:</p>
      
      <ul class="list-disc pl-6 mb-6 space-y-2">
        <li><strong>Computational Thinking:</strong> How to break down problems.</li>
        <li><strong>Data Literacy:</strong> Understanding that AI is only as good as the data it's fed.</li>
        <li><strong>Ethical Frameworks:</strong> Asking "should we build this?" not just "can we build this?"</li>
      </ul>
      
      <h2 class="text-2xl font-bold text-[#0A2540] mb-4 mt-8">Preparing for the Unknown</h2>
      
      <p class="mb-6">We cannot predict exactly what jobs will exist in 2035. But we know that almost all of them will involve AI. By demystifying these tools early, we inoculate children against fear and empower them with agency.</p>
    `
  };
  return <div className="pt-20 min-h-screen bg-white" data-id="element-325">
      {/* Progress Bar (Simulated) */}
      <div className="fixed top-0 left-0 h-1 bg-[#FF6B35] z-50 w-1/3" data-id="element-326"></div>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-id="element-327">
        <Link to="/blog" className="inline-flex items-center text-gray-500 hover:text-[#0A2540] mb-8 transition-colors" data-id="element-328">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-329" /> Back to Blog
        </Link>

        <div className="mb-8" data-id="element-330">
          <div className="flex items-center space-x-2 text-sm text-gray-500 mb-4" data-id="element-331">
            <span data-id="element-332">{post.date}</span>
            <span data-id="element-333">•</span>
            <span data-id="element-334">{post.readTime}</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold text-[#0A2540] mb-6 leading-tight" data-id="element-335">
            {post.title}
          </h1>
          <div className="flex items-center" data-id="element-336">
            <div className="w-10 h-10 bg-gray-200 rounded-full mr-3" data-id="element-337"></div>
            <div data-id="element-338">
              <div className="font-bold text-[#0A2540]" data-id="element-339">{post.author}</div>
              <div className="text-xs text-gray-500" data-id="element-340">{post.role}</div>
            </div>
          </div>
        </div>

        <img src={post.image} alt={post.title} className="w-full h-[400px] object-cover rounded-2xl mb-12 shadow-lg" data-id="element-341" />

        <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed" dangerouslySetInnerHTML={{
        __html: post.content
      }} data-id="element-342" />

        {/* Share */}
        <div className="border-t border-gray-100 mt-12 pt-8 flex items-center justify-between" data-id="element-343">
          <div className="font-bold text-[#0A2540]" data-id="element-344">Share this article</div>
          <div className="flex space-x-4" data-id="element-345">
            <button className="p-2 rounded-full bg-gray-100 hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-colors" data-id="element-346">
              <Twitter className="w-5 h-5" data-id="element-347" />
            </button>
            <button className="p-2 rounded-full bg-gray-100 hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-colors" data-id="element-348">
              <Linkedin className="w-5 h-5" data-id="element-349" />
            </button>
            <button className="p-2 rounded-full bg-gray-100 hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-colors" data-id="element-350">
              <Facebook className="w-5 h-5" data-id="element-351" />
            </button>
            <button className="p-2 rounded-full bg-gray-100 hover:bg-blue-50 text-gray-600 hover:text-blue-600 transition-colors" data-id="element-352">
              <Share2 className="w-5 h-5" data-id="element-353" />
            </button>
          </div>
        </div>
      </article>

      <Section background="gray" data-id="element-354">
        <div className="text-center" data-id="element-355">
          <h2 className="text-2xl font-bold text-[#0A2540] mb-6" data-id="element-356">
            Enjoyed this read?
          </h2>
          <p className="text-gray-600 mb-8" data-id="element-357">
            Subscribe to our newsletter for more insights.
          </p>
          <div className="flex max-w-md mx-auto gap-2" data-id="element-358">
            <input type="email" placeholder="Enter your email" className="flex-1 px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#0A2540]" data-id="element-359" />
            <Button data-id="element-360">Subscribe</Button>
          </div>
        </div>
      </Section>
    </div>;
};